import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useUnits } from "../lib/useUnits";
import { relativeDayLabel } from "../lib/date";
import { hapticSuccess } from "../lib/haptics";

interface Props {
  latestWeightKg: number | null;
  /** The day that reading was actually taken. */
  latestWeightOn: string | null;
  /** Whether a weight exists for the day being shown. */
  weighedToday: boolean;
  targetWeightKg: number;
  selectedDate: string;
  onChange: () => void | Promise<void>;
}

const STEP = 0.1;
const MIN = 20;
const MAX = 400;
const DEBOUNCE_MS = 600;

export function WeightCard({
  latestWeightKg, latestWeightOn, weighedToday, targetWeightKg, selectedDate, onChange,
}: Props) {
  const t = useT();
  const units = useUnits();
  const navigate = useNavigate();
  const [value, setValue] = useState<number | null>(latestWeightKg);
  // Optimistic: the moment you confirm or nudge, this day counts as weighed.
  const [logged, setLogged] = useState(weighedToday);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Resync when the server value changes (date switch / reload).
  useEffect(() => { setValue(latestWeightKg); }, [latestWeightKg]);
  useEffect(() => { setLogged(weighedToday); }, [weighedToday, selectedDate]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  if (value == null) {
    return (
      <button
        onClick={() => navigate("/progress")}
        className="w-full glass rounded-2xl p-4 flex items-center justify-between press"
      >
        <span className="text-sm font-medium">{t("weight.card_title")}</span>
        <span className="text-sm text-brand font-semibold">{t("weight.log_cta")} ›</span>
      </button>
    );
  }

  function save(next: number, immediate = false) {
    if (timer.current) clearTimeout(timer.current);
    const write = () => {
      void api
        .upsertMeasurement({
          measured_on: selectedDate,
          weight_kg: Number(next.toFixed(1)),
          waist_cm: null, steps: null, sleep_hours: null, note: null,
        })
        .then(() => onChange());
    };
    if (immediate) write();
    else timer.current = setTimeout(write, DEBOUNCE_MS);
  }

  function bump(delta: number) {
    // Where the picker opens on the very first nudge: the target if one
    // exists, else a neutral mid-range weight. Never displayed as data —
    // profiles seed empty now, so a 0 target means "not set".
    const base = value ?? (targetWeightKg > 0 ? targetWeightKg : 75);
    const next = Math.min(MAX, Math.max(MIN, Math.round((base + delta) * 10) / 10));
    setValue(next);
    setLogged(true); // changing the number IS logging it for this day
    save(next);
  }

  // Confirming an unchanged weight. The alternative — writing it automatically
  // — would put readings in the database that were never taken, and those
  // copies are flat by definition: they drag the trend regression toward zero,
  // which lowers measured TDEE and hands back a smaller budget. One tap keeps
  // it an assertion the user made.
  function confirm() {
    if (value == null) return;
    setLogged(true);
    hapticSuccess();
    save(value, true);
  }

  const carried = !logged && latestWeightOn !== null && latestWeightOn !== selectedDate;

  return (
    // − value + : the value sits between its two controls, so the thing you are
    // changing is what your thumbs move around. 56px targets per UI plan §4.3
    // (primary daily action), up from 36px.
    <div className="glass rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-3">
        <button
          onClick={() => bump(-STEP)}
          aria-label={t("weight.decrease")}
          className="w-14 h-14 shrink-0 rounded-full bg-surface-strong text-2xl leading-none press"
        >
          −
        </button>

        <button onClick={() => navigate("/progress")} className="flex-1 min-w-0 text-center press">
          {/* A carried-forward reading is dimmed, so a number from Tuesday can
              never be mistaken for one taken today. */}
          <div className={`text-2xl font-bold tabular-nums ${carried ? "text-fg-muted" : ""}`}>
            {units.weightParts(value).value} <span className="text-sm text-fg-muted font-medium">{units.weightParts(value).unit}</span>
          </div>
          {/* Goal and distance together: the card answers "where am I and how
              far is left" without a trip to Progress. */}
          {targetWeightKg > 0 && (
            <div className="text-sm text-fg-muted mt-0.5">
              {t("weight.goal_label", { kg: units.weight(targetWeightKg) })}
              {Math.abs(value - targetWeightKg) >= 0.1 && (
                <> · {t("weight.to_go", { kg: units.weight(Math.abs(value - targetWeightKg)) })}</>
              )}
            </div>
          )}
        </button>

        <button
          onClick={() => bump(+STEP)}
          aria-label={t("weight.increase")}
          className="w-14 h-14 shrink-0 rounded-full bg-surface-strong text-2xl leading-none press"
        >
          +
        </button>
      </div>

      {carried && (
        <button
          onClick={confirm}
          className="w-full min-h-[44px] rounded-xl bg-surface-strong px-3 py-2 flex items-center justify-between gap-3 press"
        >
          <span className="text-sm text-fg-muted text-left">
            {t("weight.from_day", { day: relativeDayLabel(latestWeightOn!, selectedDate, t) })}
          </span>
          <span className="text-sm font-semibold text-brand shrink-0">
            {t("weight.confirm_same", { kg: value.toFixed(1) })}
          </span>
        </button>
      )}
    </div>
  );
}
